import * as React from 'react';
import { styled, type StyledDecorator } from '@glitz/react';
import { imageWidth } from '@avensia/excite-ui';
import { AspectRatio, createAspectRatioDecorator } from 'webapp/decorators/aspect-ratio';
import { type ImageUrlOptions, imageIsPreloaded, useImageUrls, useShouldEagerLoadImage } from '@avensia/excite-ui';
import {
  findBrowserCachedResource,
  rememberBrowserCachedResource,
  showCacheImageUntilOriginalIsLoaded,
} from '@avensia/excite-ui';
import noImage from './noimage';
import { useImagePreset, useImageViewWidth } from './image-sizes';

type Merge<T, S> = Pick<T, Exclude<keyof T, keyof S>> & S;

export type ImagePropType = Merge<
  React.ImgHTMLAttributes<HTMLImageElement>,
  {
    image: Excite.ContentApi.ImageApiModel;
    preset?: Excite.ContentApi.ImagePreset;
    external?: boolean;
  }
>;
type PropType = React.ImgHTMLAttributes<HTMLImageElement> & {
  url: string | undefined;
  originalWidth?: number;
  originalHeight?: number;
  options?: ImageUrlOptions;
  preset?: Excite.ContentApi.ImagePreset;
};

const ImgComponent = React.forwardRef<HTMLImageElement, PropType>(
  ({ url, originalHeight, originalWidth, options = {}, sizes, preset, ...restProps }, ref) => {
    const fallbackRef = React.useRef<HTMLImageElement>(null);
    const imageRef = (ref as React.RefObject<HTMLImageElement | null>) ?? fallbackRef;
    const isPreloaded = imageIsPreloaded();
    const internalPreset = useImagePreset();
    const imageViewWidth = useImageViewWidth();
    const internalSizes = sizes ?? imageViewWidth;
    options.preset = preset ?? internalPreset;

    const { src, srcSet } = useImageUrls(url, imageWidth[options.preset], imageRef, options);
    const eager = useShouldEagerLoadImage(url, options?.forceEagerLoading);
    const cachedSrc = url ? findBrowserCachedResource(url)?.src : undefined;
    const useCachedSrc = Boolean(cachedSrc);
    showCacheImageUntilOriginalIsLoaded(src, cachedSrc, imageRef, srcSet);

    return (
      <ImageBase
        // We set the images opacity to 1 in excite/server/html-skeleton.tsx, to get the images to fade in.
        // React 18 doesn't like this since it causes a mismatch between what is rendered on the server and on the client.
        // We know what we are doing here, hence we suppress the warning.
        suppressHydrationWarning
        loading={eager ? 'eager' : 'lazy'}
        ref={imageRef}
        key={url}
        css={(useCachedSrc || isPreloaded) && cachedImageDecorator}
        {...restProps}
        height={originalHeight}
        width={originalWidth}
        srcSet={useCachedSrc ? undefined : srcSet}
        src={useCachedSrc ? cachedSrc : src}
        sizes={internalSizes}
        onLoad={e => {
          e.currentTarget.style.opacity = '1';
          if (url) {
            rememberBrowserCachedResource(e.currentTarget, url);
          }
          restProps.onLoad?.(e);
        }}
        onError={e => {}}
      />
    );
  },
);

const cachedImageDecorator = styled({
  opacity: 1,
});

const ImageBase = styled.img({
  maxWidth: '100%',
  height: 'auto',
  opacity: 0,
  transition: {
    property: 'opacity',
    duration: 200,
    timingFunction: 'ease-in',
  },
});

const ImageWithSource = styled(({ image, external, ...restProps }: ImagePropType) => {
  return (
    <ImgComponent
      alt={image.alt}
      {...restProps}
      originalHeight={image.height}
      originalWidth={image.width}
      url={image.url}
      options={{ external }}
    />
  );
});

const Image = React.memo(
  styled(({ image, ...restProps }: Partial<ImagePropType>) => {
    if (!image) {
      return null;
    }

    return <ImageWithSource image={image} {...restProps} />;
  }),
);

export const ImageWithBackgroundColor = React.memo(
  styled((props: Partial<ImagePropType> & { imageCss?: StyledDecorator }) => {
    return <SquareImage {...props} css={backgroundDecorator} />;
  }),
);

export const SquareImage = React.memo(
  styled(({ imageCss, ...props }: Partial<ImagePropType> & { imageCss?: StyledDecorator }) => {
    if (!props.image) {
      return (
        <ImagePlaceholder onClick={props.onClick}>
          <NoImage url={undefined} />
        </ImagePlaceholder>
      );
    }

    return (
      <ImagePlaceholder onClick={props.onClick}>
        <Image css={styled(squareDecorator, imageCss)} {...props} />
      </ImagePlaceholder>
    );
  }),
);

export const ImagePlaceholder = styled.div(createAspectRatioDecorator(AspectRatio['1:1']), {
  position: 'relative',
});

const backgroundDecorator = styled({
  ':after': {
    content: "''",
    backgroundColor: 'rgba(128, 122, 106, 0.06)',
    position: 'absolute',
    right: 0,
    left: 0,
    bottom: 0,
    top: 0,
  },
});

const squareDecorator = styled({
  position: 'absolute',
  objectFit: 'contain',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
});

const NoImage = styled(ImgComponent, squareDecorator);

export default Image;
